import React, { useEffect, useRef } from 'react';
import type { EventData } from '../types';
import { VERSION_INFO } from '../version';

interface NavigationDrawerProps {
  sections: EventData['homepage']['sections'];
  onNavigate: (type: string, pageId: number | null, url: string | null) => void;
  lastUpdated?: string | null;
}

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({ sections, onNavigate, lastUpdated = null }) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  const isSectionVisible = (section: typeof sections[0]) => {
    const now = new Date();

    if (section.showAt) {
      const showAtDate = new Date(section.showAt);
      if (now >= showAtDate) return true;
    }

    if (section.hideAt) {
      const hideAtDate = new Date(section.hideAt);
      if (now >= hideAtDate) return false;
    }

    return section.isVisible;
  };

  const visibleSections = [...sections]
    .filter(isSectionVisible)
    .sort((a, b) => a.order - b.order);

  const handleSectionClick = (section: typeof sections[0]) => {
    onNavigate(section.type, section.pageId, section.url);
    // Close the drawer by triggering the hide button
    const closeButton = document.querySelector('[data-drawer-hide="navigation-drawer"]') as HTMLElement;
    if (closeButton) {
      closeButton.click();
    }
  };

  return (
    <div
      ref={drawerRef}
      id="navigation-drawer"
      className="bg-white fixed top-0 right-0 z-40 bottom-0 transition-transform translate-x-full w-80 border-l border-gray-200 flex flex-col"
      tabIndex={-1}
      aria-labelledby="drawer-navigation-label"
      aria-modal="true"
      role="dialog"
    >
      <div className="p-4 flex-shrink-0">
        <h5
          id="drawer-navigation-label"
          className="text-base font-semibold text-gray-500 uppercase border-b-1 border-gray-200 pb-2"
        >
          Menü
        </h5>
        <button
          type="button"
          data-drawer-hide="navigation-drawer"
          aria-controls="navigation-drawer"
          className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 inline-flex items-center justify-center"
        >
          <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
          </svg>
          <span className="sr-only">Menü schließen</span>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col">
        <ul className="space-y-2 font-medium mb-7">
          {visibleSections.map(section => {
            const iconStyle = {
              fontVariationSettings: `'FILL' ${section.iconFill ? 1 : 0}, 'wght' ${section.iconWeight || 400}, 'GRAD' ${section.iconGrade || 0}, 'opsz' ${section.iconOpticalSize || 20}`,
              fontSize: '24px',
              color: section.iconColor || '#000000',
            };

            return (
              <li key={section.id}>
                <button
                  onClick={() => handleSectionClick(section)}
                  className="flex items-center w-full p-2 text-gray-900 rounded-lg hover:bg-gray-100 group text-left gap-3"
                >
                  {section.image ? (
                    <img src={section.image} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                  ) : section.iconName ? (
                    <span className="material-symbols-outlined flex-shrink-0" style={iconStyle}>
                      {section.iconName}
                    </span>
                  ) : (
                    <div className="w-6 h-6 flex-shrink-0" />
                  )}
                  <span>{section.title}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div
          className="mt-auto pt-3 text-xs text-gray-500 text-center border-t border-gray-200">
          <div>Version: {VERSION_INFO.version}</div>
          <div>{VERSION_INFO.buildDate}</div>
          {lastUpdated && (
            <div className="mt-2">Aktualisiert: {new Date(lastUpdated).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavigationDrawer;
